import React, { useEffect, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { trackEvent } from '@/components/PGTracker';

function toEmbed(url) {
  if (!url) return url;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

function Hero({ kicker, title, blurb, accent = 'pink' }) {
  const glow = accent === 'emerald' ? 'pg-text-glow-emerald' : accent === 'gold' ? 'pg-text-glow-gold' : 'pg-text-glow-pink';
  const num = accent === 'emerald' ? 'pg-neon-text-emerald' : accent === 'gold' ? 'pg-neon-text-gold' : 'pg-neon-text';
  return (
    <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-8 sm:pb-10">
      <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-2.5 py-1">
        <motion.p initial={{ opacity: 1, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className={`font-mono text-[11px] tracking-[0.32em] ${glow}`} style={{ textShadow: '0 2px 14px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)' }}>{kicker}</motion.p>
      </span>
      <div className="mt-2">
        <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
          <motion.h1 initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className={`font-display text-6xl sm:text-7xl lg:text-8xl tracking-[0.04em] ${num}`} style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.95)' }}>{title}</motion.h1>
        </span>
      </div>
      {blurb && (
        <div className="mt-3">
          <span className="inline-block bg-[rgba(10,8,16,0.5)] rounded-md px-3 py-1.5">
            <motion.p initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }} className="text-[var(--pg-ink)] max-w-2xl text-base sm:text-lg leading-relaxed" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.85)' }}>{blurb}</motion.p>
          </span>
        </div>
      )}
      <div className="pg-hairline mt-8" />
    </header>
  );
}

function EmptyState({ title, copy }) {
  return (
    <div className="pg-card p-10 sm:p-14 text-center max-w-2xl mx-auto" data-testid="empty-state">
      <p className="font-mono text-[11px] tracking-[0.32em] pg-text-glow-emerald">STANDBY</p>
      <h3 className="font-display text-3xl mt-3 text-[var(--pg-ink)] tracking-[0.04em]">{title}</h3>
      {copy && <p className="text-[var(--pg-ink)] mt-3">{copy}</p>}
    </div>
  );
}

export default function WatchPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get('/videos').then(({ data }) => { setItems(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const featured = items.find((v) => v.featured) || items[0];
  const rest = items.filter((v) => v.id !== featured?.id);

  return (
    <div>
      <Hero kicker="CHANNEL 01" title="WATCH" blurb="Videos, interviews, podcast clips, and visual stories. Find a seat. The projector is warm." accent="pink" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        {loading ? (
          <div className="pg-card p-10 text-center text-[var(--pg-ink-muted)]">Tuning in…</div>
        ) : items.length === 0 ? (
          <EmptyState title="OFF AIR" copy="Videos will be screened here. Subscribe to be told when the cameras roll." />
        ) : (
          <>
            {featured && (
              <button onClick={() => setOpen(featured)} data-testid="watch-featured" className="pg-card pg-card-hover overflow-hidden block w-full text-left group mb-6">
                <div className="grid md:grid-cols-2 gap-0 items-stretch">
                  <div className="relative aspect-video md:aspect-auto bg-black overflow-hidden">
                    {featured.poster_url ? (
                      <img src={mediaUrl(featured.poster_url)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[var(--pg-navy-850)] to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[rgba(7,8,15,0.4)]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="h-16 w-16 rounded-full border border-[rgba(255,62,165,0.7)] bg-[rgba(255,62,165,0.12)] flex items-center justify-center" style={{ boxShadow: '0 0 28px rgba(255,62,165,0.4)' }}>
                        <Play className="text-[var(--pg-neon-pink)] ml-1" size={22} />
                      </span>
                    </div>
                  </div>
                  <div className="p-6 sm:p-8 flex flex-col justify-center">
                    <p className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-pink">FEATURED</p>
                    <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[var(--pg-ink)] tracking-[0.04em]">{featured.title}</h2>
                    {featured.description && <p className="text-[var(--pg-ink)] mt-3 leading-relaxed">{featured.description}</p>}
                    <p className="mt-5 text-xs font-mono tracking-[0.24em] pg-text-glow-pink">PRESS PLAY →</p>
                  </div>
                </div>
              </button>
            )}

            {rest.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((it, i) => (
                  <motion.button
                    key={it.id}
                    onClick={() => setOpen(it)}
                    data-testid="watch-video-card"
                    initial={{ opacity: 1, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
                    className="pg-card pg-card-hover overflow-hidden text-left group"
                  >
                    <div className="relative aspect-video bg-black overflow-hidden">
                      {it.poster_url ? (
                        <img src={mediaUrl(it.poster_url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[var(--pg-navy-850)] to-black" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[rgba(7,8,15,0.85)]" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="h-12 w-12 rounded-full border border-[rgba(255,62,165,0.7)] bg-[rgba(255,62,165,0.10)] flex items-center justify-center" style={{ boxShadow: '0 0 18px rgba(255,62,165,0.35)' }}>
                          <Play className="text-[var(--pg-neon-pink)] ml-0.5" size={18} />
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-xl text-[var(--pg-ink)] line-clamp-2">{it.title}</h3>
                      {it.description && <p className="text-sm text-[var(--pg-ink)] mt-2 line-clamp-2">{it.description}</p>}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <AnimatePresence>
        {open && <VideoModal open={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </div>
  );
}

function VideoModal({ open, onClose }) {
  useEffect(() => {
    if (open?.id) {
      trackEvent({
        event_type: 'media_start',
        content_type: 'video',
        content_id: open.id,
        meta: { title: open.title || '' },
      });
    }
  }, [open?.id]);
  return (
    <motion.div initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-50 bg-[rgba(7,8,15,0.94)] backdrop-blur-md flex items-center justify-center p-4">
      <button className="absolute top-5 right-5 text-[var(--pg-ink)] hover:text-[var(--pg-neon-pink)]" onClick={onClose} aria-label="close"><X /></button>
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
        className="max-w-5xl w-full pg-card overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video bg-black">
          <iframe title={open.title} src={toEmbed(open.embed_url)} width="100%" height="100%" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen style={{ display: 'block', width: '100%', height: '100%' }} />
        </div>
        <div className="p-5 sm:p-6">
          <p className="pg-text-glow-pink font-mono text-[10px] tracking-[0.32em]">WATCH</p>
          <h3 className="font-display text-2xl mt-2 text-[var(--pg-ink)] tracking-[0.04em]">{open.title}</h3>
          {open.description && <p className="text-sm text-[var(--pg-ink)] mt-2">{open.description}</p>}
        </div>
      </motion.div>
    </motion.div>
  );
}

export { Hero as ChannelPageHero, EmptyState as ChannelEmpty };
